import type {Job, JobListQuery} from '@/api/jobs';

/**
 One independently paged section of the Investigations sidebar. The sidebar used to page through
 every visible job and group them client-side; each section now asks the server for its own jobs,
 and only when it is on screen.

 - `unfiled`: jobs in no folder (the sidebar's ungrouped list)
 - `folder`: a folder's direct contents, loaded when it is expanded
 - `subtree`: a folder and everything beneath it, loaded when the folder is selected for the
   aggregate view, whose header and "Re-run all" act on the whole subtree
 */
export type JobScope =
  {readonly kind: 'unfiled'} | {readonly kind: 'folder' | 'subtree'; readonly id: string};

export const UNFILED_SCOPE: JobScope = {kind: 'unfiled'};

export function scopeKey(scope: JobScope): string {
  return scope.kind === 'unfiled' ? 'unfiled' : `${scope.kind}:${scope.id}`;
}

export function parseScopeKey(key: string): JobScope | null {
  if (key === 'unfiled') {
    return UNFILED_SCOPE;
  }

  const [kind, id] = key.split(':', 2);
  return (kind === 'folder' || kind === 'subtree') && id ? {kind, id} : null;
}

/**
 Whether the sidebar's search and chips narrow this scope. Only the job lists the analyst filters
 do: a folder's aggregate (header count, "Re-run all", the poll-while-active
 check) are read unfiltered, so they are fetched unfiltered too.
 */
export function scopeIsFiltered(scope: JobScope): boolean {
  return scope.kind === 'unfiled' || scope.kind === 'folder';
}

/** Rows per request. A subtree is read whole, so it takes the API's largest page. */
export function pageSizeFor(scope: JobScope, listPageSize: number): number {
  return scope.kind === 'subtree' ? SUBTREE_PAGE_SIZE : listPageSize;
}

/** Matches JobsController.MaxJobPageSize. */
export const SUBTREE_PAGE_SIZE = 200;

/** Paging progress for one scope. `page` is the last page loaded (0 before the first). */
export type ScopePageState = {
  readonly page: number;
  readonly hasMore: boolean;
  readonly loading: boolean;
  /** The last request failed. The scope stays retryable, resuming after `page`. */
  readonly failed?: boolean;
};

/** The sidebar's search box and chips, in the shape the server filters on. */
export type JobServerFilters = {
  readonly q: string;
  readonly strategies: ReadonlySet<string>;
  readonly policyViolations: ReadonlySet<string>;
  readonly languages: ReadonlySet<string>;
  readonly mine: boolean;
  readonly creators: ReadonlySet<string>;
  readonly scheduled: boolean;
};

/**
 Strategies with no filter chip. The client-side filter always lets these through once any type
 chip is active, so the server is asked for them too - otherwise a folder holding one would page in
 short while the client still expects to show it.
 */
export const UNCHIPPED_STRATEGIES = ['youtube_video'];

/** Stable across renders with the same filters, so it can key a reload. */
export function filtersKey(filters: JobServerFilters): string {
  return JSON.stringify([
    filters.q.trim(),
    [...filters.strategies].sort(),
    [...filters.policyViolations].sort(),
    [...filters.languages].sort(),
    filters.mine,
    filters.scheduled,
    [...filters.creators].sort()
  ]);
}

export function jobListQuery(
  scope: JobScope,
  filters: JobServerFilters,
  page: number,
  pageSize: number
): JobListQuery {
  const query: JobListQuery = {page, page_size: pageSize};
  switch (scope.kind) {
    case 'unfiled': {
      query.unfiled = true;
      break;
    }

    case 'folder': {
      query.folder_id = scope.id;
      break;
    }

    case 'subtree': {
      query.folder_id = scope.id;
      query.include_subfolders = true;
      break;
    }
  }

  if (!scopeIsFiltered(scope)) {
    return query;
  }

  const q = filters.q.trim();
  if (q) {
    query.q = q;
  }

  if (filters.strategies.size > 0) {
    query.strategy = [...filters.strategies, ...UNCHIPPED_STRATEGIES];
  }

  if (filters.policyViolations.size > 0) {
    query.policy_violation = [...filters.policyViolations];
  }

  if (filters.languages.size > 0) {
    query.language = [...filters.languages];
  }

  if (filters.mine) {
    query.mine = true;
  }

  if (filters.creators.size > 0) {
    query.created_by = [...filters.creators];
  }

  if (filters.scheduled) {
    query.scheduled = true;
  }

  return query;
}

/**
 Folds a freshly fetched page into the loaded jobs. Sections share one list - a job can be fetched
 by both its folder and that folder's subtree - so an incoming job replaces any loaded copy in place
 (keeping the list's order) and only genuinely new jobs are appended. Jobs the analyst dismissed
 locally stay out. Returns `previous` itself when nothing changed, so a no-op page doesn't
 re-render the sidebar.
 */
export function mergeJobs(previous: Job[], incoming: Job[], dismissed: ReadonlySet<string>): Job[] {
  const fresh = new Map(incoming.filter((j) => !dismissed.has(j.id)).map((j) => [j.id, j]));
  if (fresh.size === 0) {
    return previous;
  }

  let changed = false;
  const merged = previous.map((j) => {
    const update = fresh.get(j.id);
    if (!update) {
      return j;
    }

    fresh.delete(j.id);
    if (update !== j) {
      changed = true;
    }

    return update;
  });
  if (fresh.size > 0) {
    changed = true;
    merged.push(...fresh.values());
  }

  return changed ? merged : previous;
}

/** Matches JobsController.MaxJobIdsPerRequest: a hundred GUIDs keep the query string near 4KB. */
export const JOB_IDS_PER_REQUEST = 100;

export function chunked<T>(values: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }

  return chunks;
}
